'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

export interface PDFViewerFile {
  name: string;
  file: File;
  pageCount: number;
}

export interface PDFViewerModalProps {
  /** The PDF file to display */
  file: PDFViewerFile;
  /** Whether the modal is visible */
  isOpen: boolean;
  /** The 0-based page index currently shown */
  currentPage: number;
  /** Called when the user requests to close the modal */
  onClose: () => void;
  /** Called when the user navigates to a different page */
  onPageChange: (page: number) => void;

  // ── Optional per-tool actions ──────────────────────────────────────────────

  /** Current page's applied rotation (0 | 90 | 180 | 270). Drives canvas rendering + indicator. */
  pageRotation?: number;

  /** Show Delete / Restore button. Both callbacks required together. */
  onDelete?: (pageIndex: number) => void;
  onRestore?: (pageIndex: number) => void;
  isPageDeleted?: (pageIndex: number) => boolean;

  /** Show Rotate button (and optional Revert). */
  onRotate?: (pageIndex: number) => void;
  onRevertRotate?: (pageIndex: number) => void;
  isPageRotated?: (pageIndex: number) => boolean;
}

/**
 * PDFViewerModal
 *
 * Full-screen, tool-agnostic PDF page viewer.
 * Pass only the action props your tool needs — the UI adapts automatically.
 */
export default function PDFViewerModal({
  file,
  isOpen,
  currentPage,
  onClose,
  onPageChange,
  pageRotation = 0,
  onDelete,
  onRestore,
  isPageDeleted,
  onRotate,
  onRevertRotate,
  isPageRotated,
}: PDFViewerModalProps) {
  const [zoom, setZoom] = useState(1);
  const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  // Intrinsic rotation embedded in the PDF page (/Rotate key).
  // Populated after the page object is loaded in the render effect.
  const [pageIntrinsicRotation, setPageIntrinsicRotation] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const renderCountRef = useRef(0);
  const renderTaskRef = useRef<any>(null);
  const dragStart = useRef({ x: 0, y: 0 });

  // ── PDF Rendering ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen || !file) return;

    let cancelled = false;

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    renderCountRef.current += 1;
    const myCount = renderCountRef.current;

    const renderPage = async () => {
      try {
        const pdfjsLib = (window as any).pdfjsLib;
        if (!pdfjsLib || cancelled) return;

        const bytes = await file.file.arrayBuffer();
        if (cancelled || renderCountRef.current !== myCount) return;

        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (cancelled || renderCountRef.current !== myCount) return;

        const page = await pdf.getPage(currentPage + 1);
        if (cancelled || renderCountRef.current !== myCount) return;

        const cw = containerRef.current?.clientWidth ?? window.innerWidth;
        const ch = containerRef.current?.clientHeight ?? window.innerHeight;

        // pdf.js: page.rotate is the page's intrinsic rotation from PDF metadata.
        // getViewport({ rotation }) REPLACES (not adds to) that intrinsic rotation,
        // so we must combine both to get the correct total visual orientation.
        const intrinsic = (page.rotate as number) ?? 0;
        setPageIntrinsicRotation(intrinsic);
        const totalRotation = (intrinsic + pageRotation) % 360;

        const naturalVp = page.getViewport({ scale: 1, rotation: totalRotation });
        const baseScale = Math.min((cw - 8) / naturalVp.width, (ch - 8) / naturalVp.height, 1);
        const viewport = page.getViewport({
          scale: Math.max(0.1, baseScale * zoom),
          rotation: totalRotation,
        });
        const dpr = window.devicePixelRatio || 1;

        // Off-screen canvas to avoid "already rendering" race condition
        const offscreen = document.createElement('canvas');
        offscreen.width = viewport.width * dpr;
        offscreen.height = viewport.height * dpr;

        const offCtx = offscreen.getContext('2d');
        if (!offCtx || cancelled || renderCountRef.current !== myCount) return;
        offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const task = page.render({ canvasContext: offCtx, viewport });
        renderTaskRef.current = task;

        await task.promise;
        renderTaskRef.current = null;

        if (cancelled || renderCountRef.current !== myCount) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = offscreen.width;
        canvas.height = offscreen.height;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const ctx = canvas.getContext('2d');
        if (!ctx || cancelled || renderCountRef.current !== myCount) return;
        ctx.drawImage(offscreen, 0, 0);
      } catch (err: any) {
        if (err?.name !== 'RenderingCancelledException') console.error(err);
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [isOpen, currentPage, zoom, file, pageRotation]);

  // ── Keyboard navigation ────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight') onPageChange(Math.min(file.pageCount - 1, currentPage + 1));
      if (e.key === 'ArrowLeft') onPageChange(Math.max(0, currentPage - 1));
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, currentPage, file, onPageChange, onClose]);

  // ── Reset zoom/pan on page change or modal close ───────────────────────────

  useEffect(() => {
    setPan({ x: 0, y: 0 });
    setZoom(1);
    setPageIntrinsicRotation(0);
  }, [currentPage]);

  useEffect(() => {
    if (!isOpen) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  }, [isOpen]);

  // ── Pan clamping ───────────────────────────────────────────────────────────

  /**
   * Clamps pan so the canvas never scrolls fully out of view.
   * maxPan = half the overflow in each axis.
   */
  const clampPan = useCallback((x: number, y: number): { x: number; y: number } => {
    if (!canvasRef.current || !containerRef.current) return { x, y };
    const maxX = Math.max(
      0,
      (canvasRef.current.offsetWidth - containerRef.current.clientWidth) / 2
    );
    const maxY = Math.max(
      0,
      (canvasRef.current.offsetHeight - containerRef.current.clientHeight) / 2
    );
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, []);

  // ── Derived state ──────────────────────────────────────────────────────────

  const pageDeleted = isPageDeleted?.(currentPage) ?? false;
  const pageRotated = isPageRotated?.(currentPage) ?? false;
  const showDelete = !!onDelete && !!onRestore;
  const showRotate = !!onRotate;
  // Total visual rotation = PDF's embedded rotation + user-applied rotation
  const totalRotation = (pageIntrinsicRotation + pageRotation) % 360;

  if (!isOpen) return null;

  // ── Zoom helpers ───────────────────────────────────────────────────────────

  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, parseFloat((z - 0.2).toFixed(1))));
  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, parseFloat((z + 0.2).toFixed(1))));

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    /**
     * Layout: fixed full-screen flex column.
     * - Top bar: shrink-0 → never compressed, always visible
     * - Canvas area: flex-1 min-h-0 → fills remaining space; min-h-0 is
     *   critical: it prevents the flex child from growing past the parent,
     *   keeping controls pinned at their positions regardless of zoom/canvas size.
     * - Mobile controls: shrink-0 → never compressed, always at bottom
     */
    <div className="fixed inset-0 z-50 bg-[#202124] flex flex-col">
      {/* ── Top Bar ── */}
      <div
        className="shrink-0 flex items-center justify-between px-4 bg-[#2b2c2f] text-white"
        style={{ paddingTop: 'max(8px, env(safe-area-inset-top))', paddingBottom: '8px' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm truncate max-w-[55vw]">{file.name}</span>
          {/* Rotation badge — shows total effective rotation including embedded PDF rotation */}
          {totalRotation !== 0 && (
            <span className="shrink-0 text-[11px] font-bold text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-full">
              {totalRotation}°
            </span>
          )}
        </div>

        <button
          onClick={onClose}
          className="shrink-0 p-2 rounded-md hover:bg-white/10 touch-manipulation ml-2"
          aria-label="Close viewer"
        >
          <Icon name="XMarkIcon" size={22} />
        </button>
      </div>

      {/* ── Canvas Area ── */}
      {/*
        flex-1 min-h-0: fills remaining height without letting the canvas push
        neighbouring elements (top bar / mobile controls) out of position.
        overflow-hidden clips the canvas at the boundary.
        relative allows the desktop pill to float inside.
      */}
      <div className="flex-1 min-h-0 overflow-hidden relative" ref={containerRef}>
        <div
          className="w-full h-full flex items-center justify-center select-none"
          style={{
            touchAction: 'none',
            cursor: isDraggingCanvas ? 'grabbing' : zoom > 1 ? 'grab' : 'default',
          }}
          onMouseDown={(e) => {
            if (zoom <= 1) return;
            setIsDraggingCanvas(true);
            dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
          }}
          onMouseMove={(e) => {
            if (!isDraggingCanvas) return;
            setPan(clampPan(e.clientX - dragStart.current.x, e.clientY - dragStart.current.y));
          }}
          onMouseUp={() => setIsDraggingCanvas(false)}
          onMouseLeave={() => setIsDraggingCanvas(false)}
          onTouchStart={(e) => {
            if (e.touches.length === 1) {
              setIsDraggingCanvas(true);
              dragStart.current = {
                x: e.touches[0].clientX - pan.x,
                y: e.touches[0].clientY - pan.y,
              };
            }
          }}
          onTouchMove={(e) => {
            if (!isDraggingCanvas || e.touches.length !== 1) return;
            e.preventDefault();
            setPan(
              clampPan(
                e.touches[0].clientX - dragStart.current.x,
                e.touches[0].clientY - dragStart.current.y
              )
            );
          }}
          onTouchEnd={() => setIsDraggingCanvas(false)}
        >
          <canvas
            ref={canvasRef}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px)`,
              transition: isDraggingCanvas ? 'none' : 'transform 0.1s ease-out',
              maxWidth: 'none', // prevent browser from constraining oversized canvas
            }}
          />
        </div>

        {/* ── Desktop Pill (floats inside canvas area, unaffected by layout) ── */}
        <div className="hidden sm:inline-flex absolute bottom-4 left-1/2 -translate-x-1/2 z-10 items-center gap-2.5 px-4 py-2 bg-black/80 backdrop-blur-md text-white rounded-full shadow-xl pointer-events-auto">
          {/* Navigation */}
          <button
            onClick={() => onPageChange(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="disabled:opacity-30"
            aria-label="Previous page"
          >
            ←
          </button>
          <span className="text-sm tabular-nums">
            {currentPage + 1} / {file.pageCount}
          </span>
          <button
            onClick={() => onPageChange(Math.min(file.pageCount - 1, currentPage + 1))}
            disabled={currentPage === file.pageCount - 1}
            className="disabled:opacity-30"
            aria-label="Next page"
          >
            →
          </button>

          <div className="w-px h-4 bg-white/30" />

          {/* Zoom */}
          <button
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="disabled:opacity-30"
            aria-label="Zoom out"
          >
            <Icon name="MinusIcon" size={16} />
          </button>
          <span className="text-xs w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="disabled:opacity-30"
            aria-label="Zoom in"
          >
            <Icon name="PlusIcon" size={16} />
          </button>

          {/* Rotate action */}
          {showRotate && (
            <>
              <div className="w-px h-4 bg-white/30" />
              {totalRotation !== 0 && (
                <span className="text-[11px] font-bold text-amber-400 tabular-nums">
                  {totalRotation}°
                </span>
              )}
              <button
                onClick={() => onRotate!(currentPage)}
                className="text-amber-400 flex items-center gap-1 text-sm font-medium"
                aria-label="Rotate page 90°"
              >
                <Icon name="ArrowPathIcon" size={15} />
                Rotate
              </button>
              {pageRotated && onRevertRotate && (
                <button
                  onClick={() => onRevertRotate!(currentPage)}
                  className="text-slate-300 flex items-center gap-1 text-sm font-medium"
                  aria-label="Reset rotation"
                >
                  <Icon name="ArrowUturnLeftIcon" size={14} />
                </button>
              )}
            </>
          )}

          {/* Delete / Restore action */}
          {showDelete && (
            <>
              <div className="w-px h-4 bg-white/30" />
              {pageDeleted ? (
                <button
                  onClick={() => onRestore!(currentPage)}
                  className="text-green-400 text-sm font-medium"
                  aria-label="Restore page"
                >
                  Restore
                </button>
              ) : (
                <button
                  onClick={() => onDelete!(currentPage)}
                  className="text-red-400 text-sm font-medium"
                  aria-label="Delete page"
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Mobile Controls ── */}
      <div
        className="sm:hidden shrink-0 bg-black/85 backdrop-blur-md text-white"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Row 1: Navigation + tool actions */}
        <div className="flex items-center justify-between px-5 pt-3 pb-2">
          {/* Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => onPageChange(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="disabled:opacity-30 text-xl touch-manipulation p-1"
              aria-label="Previous page"
            >
              ←
            </button>
            <span className="text-sm font-medium tabular-nums">
              {currentPage + 1} / {file.pageCount}
            </span>
            <button
              onClick={() => onPageChange(Math.min(file.pageCount - 1, currentPage + 1))}
              disabled={currentPage === file.pageCount - 1}
              className="disabled:opacity-30 text-xl touch-manipulation p-1"
              aria-label="Next page"
            >
              →
            </button>
          </div>

          {/* Tool actions */}
          <div className="flex items-center gap-3">
            {showRotate && (
              <>
                {totalRotation !== 0 && (
                  <span className="text-[11px] font-bold text-amber-400 tabular-nums">
                    {totalRotation}°
                  </span>
                )}
                <button
                  onClick={() => onRotate!(currentPage)}
                  className="touch-manipulation text-amber-400"
                  aria-label="Rotate page"
                >
                  <Icon name="ArrowPathIcon" size={20} />
                </button>
                {pageRotated && onRevertRotate && (
                  <button
                    onClick={() => onRevertRotate!(currentPage)}
                    className="touch-manipulation text-slate-300"
                    aria-label="Reset rotation"
                  >
                    <Icon name="ArrowUturnLeftIcon" size={18} />
                  </button>
                )}
              </>
            )}
            {showDelete &&
              (pageDeleted ? (
                <button
                  onClick={() => onRestore!(currentPage)}
                  className="text-green-400 text-sm font-semibold touch-manipulation"
                  aria-label="Restore page"
                >
                  Restore
                </button>
              ) : (
                <button
                  onClick={() => onDelete!(currentPage)}
                  className="text-red-400 text-sm font-semibold touch-manipulation"
                  aria-label="Delete page"
                >
                  Delete
                </button>
              ))}
          </div>
        </div>

        {/* Row 2: Zoom */}
        <div className="flex items-center justify-center gap-5 px-5 pb-3">
          <button
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="disabled:opacity-30 touch-manipulation p-2"
            aria-label="Zoom out"
          >
            <Icon name="MinusIcon" size={20} />
          </button>
          <span className="text-sm w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="disabled:opacity-30 touch-manipulation p-2"
            aria-label="Zoom in"
          >
            <Icon name="PlusIcon" size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
